import { formatDuration, intervalToDuration } from 'date-fns';
import { useMemo } from 'react';
import Table, { Header, Row } from 'src/components/Table';
import { Call, getAgentId } from 'src/interfaces/call.interface';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber } from 'src/utils/number';

const headers: Header[] = [
  { key: 'id', label: 'Call ID', width: '20%', disableSorting: true },
  { key: 'agent_name', label: 'Agent', width: '15%', disableSorting: true },
  { key: 'start_time', label: 'Created', width: '20%' },
  { key: 'duration', label: 'Duration', width: '15%', disableSorting: true },
  {
    key: 'to_number',
    label: 'To',
    width: '15%',
    wrapValue: false,
    disableSorting: true,
  },
  {
    key: 'from_number',
    label: 'From',
    width: '15%',
    wrapValue: false,
    disableSorting: true,
  },
];

export type CallSelectorTableProps = {
  calls: Call[];
  agentNames: Record<string, string>;
  selectedCallId: string | null;
  onSelectCall: (call: Call) => void;
  totalItems: number;
  currentPage: number;
  onPageChange: (page: number, perPage: number) => void;
  onSort: (key: string, direction: string) => void;
  loading: boolean;
  pageSize?: number;
};

export const CallSelectorTable = ({
  calls,
  agentNames,
  selectedCallId,
  onSelectCall,
  totalItems,
  currentPage,
  onPageChange,
  onSort,
  loading,
  pageSize = 5,
}: CallSelectorTableProps) => {
  const rows: Row[] = useMemo(() => {
    return calls.map((call) => {
      const startDate = call.start_time
        ? new Date(Number(call.start_time) * 1000)
        : null;
      const endDate = call.end_time
        ? new Date(Number(call.end_time) * 1000)
        : null;
      const agentId = getAgentId(call.agent);

      const truncatedId =
        call.id.length > 8
          ? `${call.id.slice(0, 4)}...${call.id.slice(-4)}`
          : call.id;

      return {
        id: (
          <span className="text-xs" title={call.id}>
            {truncatedId}
          </span>
        ),
        agent_name: (
          <span className="text-xs">
            {agentId ? agentNames[agentId] || '(unknown)' : '(deleted)'}
          </span>
        ),
        start_time: (
          <span className="text-xs">{friendlyDateFormatter(startDate)}</span>
        ),
        duration: (
          <span className="text-xs">
            {startDate && endDate
              ? formatDuration(
                  intervalToDuration({ start: startDate, end: endDate }),
                  { delimiter: ', ' },
                ) || '0s'
              : '-'}
          </span>
        ),
        to_number: (
          <span className="text-xs">{processPhoneNumber(call.to_number)}</span>
        ),
        from_number: (
          <span className="text-xs">
            {processPhoneNumber(call.from_number)}
          </span>
        ),
      };
    });
  }, [calls, agentNames]);

  const selectedRowIndex = useMemo(() => {
    if (!selectedCallId) return undefined;
    return calls.findIndex((call) => call.id === selectedCallId);
  }, [calls, selectedCallId]);

  const handleRowClick = (index: number) => {
    const call = calls[index];
    if (call) {
      onSelectCall(call);
    }
  };

  return (
    <Table
      headers={headers}
      rows={rows}
      selectedRow={selectedRowIndex}
      onRowClick={handleRowClick}
      totalItems={totalItems}
      currentPage={currentPage}
      onPageChange={onPageChange}
      onSort={onSort}
      loading={loading}
      defaultSize={pageSize}
      variant="primary"
    />
  );
};
